import { gsap } from 'gsap'
import { CONFIG, ENTRY } from '@/lib/liquid-carousel/config'

/** Rise into the current layout; only selecting a look expands the overview. */
export function playTimelineEntrance(node: HTMLElement) {
  const figures = Array.from(
    node.querySelectorAll<HTMLElement>('.outfit-model-enter'),
  )
  const scene = node.closest<HTMLElement>('.outfit-timeline')!
  scene.dataset.entering = 'true'
  const timeline = gsap.timeline({
    delay: ENTRY.delay,
    onComplete: () => {
      scene.dataset.entering = 'false'
    },
  })
  figures.forEach((figure, index) => {
    timeline.fromTo(
      figure,
      { y: 180, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: ENTRY.riseDuration,
        ease: ENTRY.riseEase,
        clearProps: 'transform,opacity',
      },
      index * ENTRY.stagger,
    )
  })
  const finish = () => {
    timeline.progress(1)
    scene.dataset.entering = 'false'
  }
  node.addEventListener('pointerdown', finish, { once: true })
  return () => {
    timeline.kill()
    node.removeEventListener('pointerdown', finish)
    gsap.set(figures, { clearProps: 'transform,opacity' })
    scene.dataset.entering = 'false'
  }
}

export function wheelPixels(event: WheelEvent) {
  const delta =
    Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY
  return delta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1)
}

/** A trackpad's inertia belongs to the current gesture, not the next outfit. */
export function attachTimelineWheel(
  node: HTMLElement,
  onStep: (direction: number) => void,
) {
  let total = 0
  let stepped = false
  let idle: ReturnType<typeof setTimeout> | undefined
  const wheel = (event: WheelEvent) => {
    if (event.ctrlKey || node.closest<HTMLElement>('[data-entering="true"]'))
      return
    event.preventDefault()
    const delta = wheelPixels(event)
    if (!delta) return
    if (Math.sign(delta) !== Math.sign(total)) {
      total = 0
      stepped = false
    }
    total += delta
    clearTimeout(idle)
    idle = setTimeout(() => {
      total = 0
      stepped = false
    }, CONFIG.SNAP_IDLE_MS)
    if (!stepped && Math.abs(total) >= 40) {
      stepped = true
      onStep(Math.sign(total))
    }
  }
  node.addEventListener('wheel', wheel, { passive: false })
  return () => {
    node.removeEventListener('wheel', wheel)
    clearTimeout(idle)
  }
}

/** Read committed DOM selection so an interrupted React render cannot steer input. */
export function bindTimelineScene(node: HTMLDivElement | null) {
  if (!node) return
  const stopEntrance = playTimelineEntrance(node)
  const stopWheel = attachTimelineWheel(node, (direction) => {
    const scene = node.closest<HTMLElement>('.outfit-timeline')
    const buttons = scene?.querySelectorAll<HTMLButtonElement>(
      '.outfit-footer nav button',
    )
    buttons?.[direction < 0 ? 0 : 1]?.click()
  })
  return () => {
    stopEntrance()
    stopWheel()
  }
}
